import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const LeaderboardPanel = ({ leaderboardData, currentUser, tournamentId, tournamentName }) => {
  const [viewMode, setViewMode] = useState('overall');
  const navigate = useNavigate();

  const getMedalIcon = (rank) => {
    const medals = {
      1: { icon: 'Medal', color: '#FFD700' },
      2: { icon: 'Medal', color: '#C0C0C0' },
      3: { icon: 'Medal', color: '#CD7F32' }
    };
    return medals?.[rank] || null;
  };

  const isCurrentUser = (userId) => {
    return userId === currentUser?.id;
  };

  const formatPoints = (points) => {
    return points?.toLocaleString('en-US');
  };

  const handleViewFullLeaderboard = () => {
    if (tournamentId) {
      navigate(`/tournament-details/${tournamentId}?tab=leaderboard`);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">Leaderboard</h2>
        <div className="flex gap-1.5">
          <button
            onClick={() => setViewMode('overall')}
            className={`px-2.5 py-1.5 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-smooth ${
              viewMode === 'overall' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Overall
          </button>
          <button
            onClick={() => setViewMode('weekly')}
            className={`px-2.5 py-1.5 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-smooth ${
              viewMode === 'weekly' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Weekly
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {leaderboardData?.length > 0 ? (
          leaderboardData?.map((entry, index) => {
            const medal = getMedalIcon(entry?.rank);
            const isUser = isCurrentUser(entry?.userId);

            return (
              <div
                key={entry?.userId}
                className={`flex items-center gap-2.5 md:gap-3 p-2.5 md:p-3 rounded-lg transition-smooth min-h-[60px] ${
                  isUser
                    ? 'bg-primary/10 border-2 border-primary'
                    : index % 2 === 0
                    ? 'bg-muted/50' :'bg-background'
                } hover:bg-muted/80`}
              >
                <div className="flex-shrink-0 w-7 md:w-8 text-center">
                  {medal ? (
                    <Icon name={medal?.icon} size={20} color={medal?.color} />
                  ) : (
                    <span className="text-sm md:text-base font-semibold text-muted-foreground">
                      {entry?.rank}
                    </span>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden bg-primary/10">
                    <Image
                      src={entry?.avatar}
                      alt={entry?.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="text-xs md:text-sm font-semibold text-foreground truncate">
                      {entry?.name}
                    </h3>
                    {isUser && (
                      <span className="px-1.5 py-0.5 bg-primary text-primary-foreground text-[10px] rounded-full flex-shrink-0">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] md:text-xs text-muted-foreground">
                    {entry?.predictions} predictions
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm md:text-base font-bold text-foreground">
                    {formatPoints(entry?.points)}
                  </div>
                  <div className="flex items-center justify-end gap-0.5 text-[10px] md:text-xs">
                    {entry?.change > 0 ? (
                      <>
                        <Icon name="TrendingUp" size={12} color="var(--color-success)" />
                        <span className="text-success">+{entry?.change}</span>
                      </>
                    ) : entry?.change < 0 ? (
                      <>
                        <Icon name="TrendingDown" size={12} color="var(--color-error)" />
                        <span className="text-error">{entry?.change}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No leaderboard data available yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Rankings will appear once predictions are scored.</p>
          </div>
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-border">
        <button 
          onClick={handleViewFullLeaderboard}
          disabled={!tournamentId}
          className="w-full py-2 text-xs md:text-sm font-medium text-primary hover:text-primary/80 transition-smooth flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          View Full Leaderboard
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
    </div>
  );
};

export default LeaderboardPanel;